import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Download, Search, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { LeadRow } from '../shared';
import { useAdminCtx } from '../AdminDashboardContext';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` });

export default function LeadsTab() {
  const { assignedFilter, exportCSV, fetchLeads, leads, leadsPage, leadsPages, leadsTotal, readFilter, scoreFilter, search, setAssignedFilter, setReadFilter, setScoreFilter, setSearch, setSelectedLeadId, setSortBy, setStatusFilter, sortBy, statusFilter, team, toggleRead } = useAdminCtx();
  const [clearing, setClearing] = useState(false);

  const handleClearTestData = async () => {
    try {
      const { data: preview } = await axios.get(`${API}/api/admin/test-data/preview`, { headers: getAuthHeaders() });
      const total = preview.total ?? preview.count ?? 0;
      if (!total) {
        toast.success('No test data found — your CRM is already clean.');
        return;
      }
      const b = preview.breakdown || {};
      const lines = [
        `Leads: ${b.leads?.count ?? 0}`,
        `DISC quiz leads: ${b.disc_quiz_leads?.count ?? 0}`,
        `Assessments: ${b.assessments?.count ?? 0}`,
      ].join('\n');
      const confirmMsg = `This will permanently delete ${total} test record${total === 1 ? '' : 's'}:\n\n${lines}\n\nThis cannot be undone. Continue?`;
      if (!window.confirm(confirmMsg)) return;
      setClearing(true);
      const { data } = await axios.delete(`${API}/api/admin/test-data`, { headers: getAuthHeaders() });
      toast.success(`Cleaned ${data.total ?? data.deleted} test records.`);
      fetchLeads(1);
    } catch (err) {
      toast.error('Failed to clear test data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setClearing(false);
    }
  };
  return (
    <div data-testid="leads-section">
      {/* Search + Filters + Export */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search by name, email, org..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="leads-search-input"
            className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="leads-status-filter"
            className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none">
            <option value="all">All Stages</option>
            <option value="new">New</option><option value="contacted">Contacted</option>
            <option value="meeting_scheduled">Meeting</option><option value="proposal_sent">Proposal</option>
            <option value="won">Won</option><option value="lost">Lost</option>
            <option value="stale">Stale (48h+)</option>
          </select>
          <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)} data-testid="leads-assigned-filter"
            className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none">
            <option value="">All Owners</option>
            <option value="unassigned">Unassigned</option>
            {team.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="leads-sort"
            className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="score_desc">Highest Score</option>
            <option value="score_asc">Lowest Score</option>
            <option value="value_desc">Highest Value</option>
            <option value="value_asc">Lowest Value</option>
          </select>
          <select value={scoreFilter} onChange={(e) => setScoreFilter(e.target.value)} data-testid="leads-score-filter"
            className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none">
            <option value="">All Scores</option>
            <option value="hot">Hot (70+)</option>
            <option value="warm">Warm (40-69)</option>
            <option value="cold">Cold (0-39)</option>
          </select>
          <select value={readFilter} onChange={(e) => setReadFilter(e.target.value)} data-testid="leads-read-filter"
            className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none">
            <option value="all">All Leads</option><option value="unread">Unread</option><option value="read">Read</option>
          </select>
          <Button onClick={exportCSV} variant="outline" className="border-slate-700 text-white hover:bg-slate-800" data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button
            onClick={handleClearTestData}
            disabled={clearing}
            variant="outline"
            className="border-red-900/40 text-red-300 hover:bg-red-950/30 hover:text-red-200"
            data-testid="clear-test-data-button"
          >
            <Trash2 className="h-4 w-4 mr-2" /> {clearing ? 'Clearing…' : 'Clear Test Data'}
          </Button>
        </div>
      </div>

      {/* Total */}
      <p className="text-slate-400 text-sm mb-3">{leadsTotal} lead{leadsTotal !== 1 ? 's' : ''} found</p>

      {/* Leads Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leads-table">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Organization</th>
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Status</th>
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Score</th>
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Value</th>
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Assigned</th>
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden xl:table-cell">Next Task</th>
                <th className="px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Date</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {leads.length > 0 ? leads.map((lead) => (
                <LeadRow key={lead.id} lead={lead} onToggleRead={toggleRead} onSelect={setSelectedLeadId} />
              )) : (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-slate-500 text-sm">
                    No leads found{search && ' matching your search'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {leadsPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
            <span className="text-slate-400 text-sm">Page {leadsPage} of {leadsPages}</span>
            <div className="flex gap-2">
              <Button
                onClick={() => fetchLeads(leadsPage - 1)}
                disabled={leadsPage <= 1}
                variant="outline"
                size="sm"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="leads-prev-page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => fetchLeads(leadsPage + 1)}
                disabled={leadsPage >= leadsPages}
                variant="outline"
                size="sm"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="leads-next-page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
